import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import {
  ApiResponse,
  ApiError,
  LoginCredentials,
  RegisterData,
  AuthResponseData,
  RefreshTokenResponseData,
  User,
  UpdateUserData,
  ChangePasswordData,
  UserProperty,
  CreatePropertyData,
  UpdatePropertyData,
} from './types';
import { sessionManager } from './sessionManager';
import { getTokenExpiration } from './jwtUtils';

// Extend AxiosRequestConfig to include retry flag
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Get API base URL from environment
const getApiBaseUrl = (): string => {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined') {
    // Client-side (Vite): process.env.VITE_API_URL is replaced at build time via vite.config.ts define.
    // Must NOT use optional chaining (?.) — it breaks Vite's static string substitution.
    return process.env.VITE_API_URL || 'http://localhost:3001/api';
  } else {
    // Server-side (Next.js)
    return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';
  }
};

class ApiService {
  private client: AxiosInstance;
  private refreshTokenPromise: Promise<string> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: getApiBaseUrl(),
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      config => {
        const accessToken = this.getAccessToken();
        if (accessToken && config.headers) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Response interceptor for error handling and token refresh
    this.client.interceptors.response.use(
      response => response,
      async (error: AxiosError<ApiResponse>) => {
        const originalRequest = error.config as CustomAxiosRequestConfig;

        // Handle 401 Unauthorized - attempt token refresh
        // Skip token refresh for auth endpoints - these are credential errors, not session expiry
        const isAuthEndpoint =
          originalRequest?.url?.includes('/auth/login') ||
          originalRequest?.url?.includes('/auth/register') ||
          originalRequest?.url?.includes('/auth/social-login');

        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          !isAuthEndpoint
        ) {
          originalRequest._retry = true;

          try {
            const newAccessToken = await this.handleTokenRefresh();
            if (newAccessToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed - show session expired modal
            this.clearAuthTokens();
            sessionManager.triggerSessionExpired();
            return Promise.reject(refreshError);
          }
        }

        // Transform error to ApiError format
        const apiError: ApiError = {
          message:
            error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            'An error occurred',
          status: error.response?.status || 500,
          code: error.code,
        };

        return Promise.reject(apiError);
      }
    );
  }

  private async handleTokenRefresh(): Promise<string> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    this.refreshTokenPromise = (async () => {
      try {
        const response = await axios.post<
          ApiResponse<RefreshTokenResponseData>
        >(`${getApiBaseUrl()}/auth/refresh-token`, { refreshToken });

        if (response.data.success && response.data.data) {
          const { accessToken, refreshToken: newRefreshToken } =
            response.data.data.tokens;
          this.setAuthTokens(accessToken, newRefreshToken);
          return accessToken;
        }

        throw new Error('Token refresh failed');
      } finally {
        this.refreshTokenPromise = null;
      }
    })();

    return this.refreshTokenPromise;
  }

  // Token management
  private getAccessToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('accessToken');
  }

  private getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('refreshToken');
  }

  private setAuthTokens(accessToken: string, refreshToken: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    // Store the token expiration time
    const expiration = getTokenExpiration(accessToken);
    if (expiration) {
      localStorage.setItem('tokenExpiration', expiration.toString());
    }
  }

  public getTokenExpiration(): number | null {
    if (typeof window === 'undefined') return null;
    const expiration = localStorage.getItem('tokenExpiration');
    return expiration ? parseInt(expiration, 10) : null;
  }

  public clearAuthTokens(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiration');
  }

  // Authentication APIs
  async register(data: RegisterData): Promise<ApiResponse<AuthResponseData>> {
    const response = await this.client.post<ApiResponse<AuthResponseData>>(
      '/auth/register',
      data
    );

    // Store tokens if registration successful
    if (response.data.success && response.data.data) {
      const { accessToken, refreshToken } = response.data.data.tokens;
      this.setAuthTokens(accessToken, refreshToken);
    }

    return response.data;
  }

  async login(
    credentials: LoginCredentials
  ): Promise<ApiResponse<AuthResponseData>> {
    const response = await this.client.post<ApiResponse<AuthResponseData>>(
      '/auth/login',
      credentials
    );

    // Store tokens if login successful
    if (response.data.success && response.data.data) {
      const { accessToken, refreshToken } = response.data.data.tokens;
      this.setAuthTokens(accessToken, refreshToken);
    }

    return response.data;
  }

  async socialLogin(idToken: string): Promise<ApiResponse<AuthResponseData>> {
    const response = await this.client.post<ApiResponse<AuthResponseData>>(
      '/auth/social-login',
      { idToken }
    );

    if (response.data.success && response.data.data) {
      const { accessToken, refreshToken } = response.data.data.tokens;
      this.setAuthTokens(accessToken, refreshToken);
    }

    return response.data;
  }

  async refreshToken(
    refreshToken: string
  ): Promise<ApiResponse<RefreshTokenResponseData>> {
    const response = await this.client.post<
      ApiResponse<RefreshTokenResponseData>
    >('/auth/refresh-token', { refreshToken });

    // Update tokens if refresh successful
    if (response.data.success && response.data.data) {
      const { accessToken, refreshToken: newRefreshToken } =
        response.data.data.tokens;
      this.setAuthTokens(accessToken, newRefreshToken);
    }

    return response.data;
  }

  async logout(): Promise<ApiResponse<void>> {
    try {
      const refreshToken = this.getRefreshToken();
      const response = await this.client.post<ApiResponse<void>>(
        '/auth/logout',
        { refreshToken }
      );
      this.clearAuthTokens();
      return response.data;
    } catch (error) {
      // Clear tokens even if API call fails
      this.clearAuthTokens();
      throw error;
    }
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await this.client.get<ApiResponse<User>>('/auth/profile');
    return response.data;
  }

  // User APIs
  async getUser(userId: string): Promise<ApiResponse<User>> {
    const response = await this.client.get<ApiResponse<User>>(
      `/users/${userId}`
    );
    return response.data;
  }

  async updateUser(
    userId: string,
    data: UpdateUserData
  ): Promise<ApiResponse<User>> {
    const response = await this.client.put<ApiResponse<User>>(
      `/users/${userId}`,
      data
    );
    return response.data;
  }

  async changePassword(data: ChangePasswordData): Promise<ApiResponse<void>> {
    const response = await this.client.post<ApiResponse<void>>(
      '/users/change-password',
      data
    );
    return response.data;
  }

  async deactivateAccount(): Promise<ApiResponse<void>> {
    const response =
      await this.client.post<ApiResponse<void>>('/users/deactivate');
    return response.data;
  }

  // Property APIs
  async getProperties(): Promise<ApiResponse<UserProperty[]>> {
    const response =
      await this.client.get<ApiResponse<UserProperty[]>>('/properties');
    return response.data;
  }

  async createProperty(
    data: CreatePropertyData
  ): Promise<ApiResponse<UserProperty>> {
    const response = await this.client.post<ApiResponse<UserProperty>>(
      '/properties',
      data
    );
    return response.data;
  }

  async updateProperty(
    propertyId: string,
    data: UpdatePropertyData
  ): Promise<ApiResponse<UserProperty>> {
    const response = await this.client.put<ApiResponse<UserProperty>>(
      `/properties/${propertyId}`,
      data
    );
    return response.data;
  }

  async deleteProperty(propertyId: string): Promise<ApiResponse<null>> {
    const response = await this.client.delete<ApiResponse<null>>(
      `/properties/${propertyId}`
    );
    return response.data;
  }

  // Public method to manually trigger token refresh (for proactive refresh)
  public async refreshAccessToken(): Promise<boolean> {
    try {
      await this.handleTokenRefresh();
      return true;
    } catch (error) {
      console.error('Failed to refresh access token:', error);
      return false;
    }
  }
}

export const apiService = new ApiService();
